/**
 * SyncStatusBadge — persistent pill on the Floor page header.
 *
 * Feb 2026. Communicates offline-sync state at a glance:
 *
 *   ● Online (green)              — nothing pending
 *   ● Syncing… (spinning blue)    — drain in flight
 *   ● Offline · N queued (amber)  — device offline, N writes pending
 *   ● N conflict(s) (rose)        — items the server refused (only
 *                                    shown when count > 0, in
 *                                    addition to the primary pill)
 *
 * Click surfaces a native `alert` listing the first few queued items
 * and any conflicts so the user can decide what to do. Kept
 * deliberately small — this is a header ornament, not a page.
 */

import React from 'react';
import { CloudOff, RefreshCw, Wifi, WifiOff } from 'lucide-react';

import useOfflineSync from '../hooks/useOfflineSync';

function pillClasses({ online, syncing, queueLen }) {
  if (!online) {
    return 'bg-amber-100 text-amber-900 border-amber-300';
  }
  if (syncing) {
    return 'bg-sky-100 text-sky-900 border-sky-300';
  }
  if (queueLen > 0) {
    return 'bg-amber-100 text-amber-900 border-amber-300';
  }
  return 'bg-emerald-100 text-emerald-900 border-emerald-300';
}

export default function SyncStatusBadge() {
  const { online, syncing, queueLen, conflicts, drain } = useOfflineSync();

  let label;
  let Icon;
  if (!online) {
    label = queueLen > 0 ? `Offline · ${queueLen} saving locally` : 'Offline';
    Icon = WifiOff;
  } else if (syncing) {
    label = `Syncing… ${queueLen ? `(${queueLen})` : ''}`.trim();
    Icon = RefreshCw;
  } else if (queueLen > 0) {
    label = `Pending · ${queueLen} queued`;
    Icon = CloudOff;
  } else {
    label = 'Online';
    Icon = Wifi;
  }

  const onClick = () => {
    if (queueLen === 0 && conflicts.length === 0) {
      // Manual force-drain — helps the user re-verify state when
      // network came back but the badge is briefly stale.
      drain();
      return;
    }
    const parts = [];
    if (queueLen > 0) parts.push(`${queueLen} pending write${queueLen === 1 ? '' : 's'} waiting to sync.`);
    if (conflicts.length > 0) {
      const brief = conflicts.slice(0, 3).map((c) => `• ${c.kind} — ${c.conflict}`).join('\n');
      parts.push(`\n${conflicts.length} conflict${conflicts.length === 1 ? '' : 's'} — server refused:\n${brief}`);
    }
    // eslint-disable-next-line no-alert
    window.alert(parts.join('\n'));
  };

  const cls = pillClasses({ online, syncing, queueLen });
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${cls}`}
      data-testid="floor-sync-status-badge"
      title={
        !online
          ? 'Device is offline — scans and completions are being saved locally.'
          : queueLen > 0
            ? 'Waiting to reach the server. Tap to try now.'
            : conflicts.length > 0
              ? `${conflicts.length} conflict(s) — tap for details.`
              : 'Live sync with server.'
      }
    >
      <Icon className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
      <span>{label}</span>
      {conflicts.length > 0 && (
        <span
          className="ml-1 inline-flex items-center rounded-full bg-rose-500 text-white text-[10px] px-1.5 py-0.5"
          data-testid="floor-sync-conflict-count"
        >
          {conflicts.length}
        </span>
      )}
    </button>
  );
}
